import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/system";
import { useEffect } from "react";
import { NumericFormat } from "react-number-format";

const StyledTextField = styled(TextField)({
  minWidth: "80px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E9EAEC",
    borderRadius: "10px"
  },
  "& .MuiInputBase-root.Mui-disabled": {
    backgroundColor: "rgb(255, 255, 255)",
    borderColor: "rgb(179, 179, 179)",
    outline: "none",
    "& fieldset": {
      borderColor: "rgb(179, 179, 179)",
      borderWidth: "0.5px"
    }
  }
});

export const Section0 = ({
  formState,
  handleChange,
  handleSectionComplete,
  reportId,
  isEditingEnabled
}) => {
  useEffect(() => {
    const mainCategories = ["baptized", "unbaptized", "children"];
    const branchCategories = [
      "baptized_in_branch",
      "unbaptized_in_branch",
      "children_in_branch"
    ];
    const allCategories = [...mainCategories, ...branchCategories];

    // Calculate all totals in one go to batch the updates
    const updates = allCategories.reduce((acc, category) => {
      const male = formState.memberCounts[category].male || 0;
      const female = formState.memberCounts[category].female || 0;
      const total = male + female;

      if (total !== formState.memberCounts[category].total) {
        acc[category] = {
          ...formState.memberCounts[category],
          total
        };
      }
      return acc;
    }, {});

    // Only update if there are changes
    if (Object.keys(updates).length > 0) {
      // Calculate grand total for all members
      const grandTotal = allCategories.reduce((sum, category) => {
        return (
          sum +
          (updates[category]?.total ||
            formState.memberCounts[category].total ||
            0)
        );
      }, 0);

      // Calculate total for branch members
      const branchTotal = branchCategories.reduce((sum, category) => {
        return (
          sum +
          (updates[category]?.total ||
            formState.memberCounts[category].total ||
            0)
        );
      }, 0);

      // Batch all updates together
      handleChange("memberCounts", "all", {
        ...updates,
        total: grandTotal,
        total_in_branches: branchTotal
      });
    }
  }, [
    formState.memberCounts.baptized.male,
    formState.memberCounts.baptized.female,
    formState.memberCounts.unbaptized.male,
    formState.memberCounts.unbaptized.female,
    formState.memberCounts.children.male,
    formState.memberCounts.children.female,
    formState.memberCounts.baptized_in_branch.male,
    formState.memberCounts.baptized_in_branch.female,
    formState.memberCounts.unbaptized_in_branch.male,
    formState.memberCounts.unbaptized_in_branch.female,
    formState.memberCounts.children_in_branch.male,
    formState.memberCounts.children_in_branch.female
  ]);

  return (
    <Stack spacing={2}>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="center">ወንድ</TableCell>
              <TableCell align="center">ሴት</TableCell>
              <TableCell align="center">ድምር</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>በአጥቢያይቱ የሚገኙ የተጠመቁ ምዕመናን ብዛት *</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.baptized.male}
                  onValueChange={values => {
                    handleChange("memberCounts", "baptized", {
                      ...formState.memberCounts.baptized,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.baptized.female}
                  onValueChange={values => {
                    handleChange("memberCounts", "baptized", {
                      ...formState.memberCounts.baptized,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.baptized.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>በአጥቢያይቱ የሚገኙ ያልተጠመቁ ምዕመናን ብዛት *</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.unbaptized.male}
                  onValueChange={values => {
                    handleChange("memberCounts", "unbaptized", {
                      ...formState.memberCounts.unbaptized,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.unbaptized.female}
                  onValueChange={values => {
                    handleChange("memberCounts", "unbaptized", {
                      ...formState.memberCounts.unbaptized,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.unbaptized.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>በአጥቢያይቱ የሚገኙ ልጆች ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.children.male}
                  onValueChange={values => {
                    handleChange("memberCounts", "children", {
                      ...formState.memberCounts.children,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.children.female}
                  onValueChange={values => {
                    handleChange("memberCounts", "children", {
                      ...formState.memberCounts.children,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.children.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>በሥርጭት ጣቢያ ውስጥ የተጠመቁ ምዕመናን ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.baptized_in_branch.male}
                  onValueChange={values => {
                    handleChange("memberCounts", "baptized_in_branch", {
                      ...formState.memberCounts.baptized_in_branch,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.baptized_in_branch.female}
                  onValueChange={values => {
                    handleChange("memberCounts", "baptized_in_branch", {
                      ...formState.memberCounts.baptized_in_branch,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.baptized_in_branch.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>በሥርጭት ጣቢያ ውስጥ የሚገኙ ያልተጠመቁ ምዕመናን ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.unbaptized_in_branch.male}
                  onValueChange={values => {
                    handleChange("memberCounts", "unbaptized_in_branch", {
                      ...formState.memberCounts.unbaptized_in_branch,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.unbaptized_in_branch.female}
                  onValueChange={values => {
                    handleChange("memberCounts", "unbaptized_in_branch", {
                      ...formState.memberCounts.unbaptized_in_branch,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.unbaptized_in_branch.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>በሥርጭት ጣቢያ ውስጥ የሚገኙ ልጆች ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.children_in_branch.male}
                  onValueChange={values => {
                    handleChange("memberCounts", "children_in_branch", {
                      ...formState.memberCounts.children_in_branch,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.children_in_branch.female}
                  onValueChange={values => {
                    handleChange("memberCounts", "children_in_branch", {
                      ...formState.memberCounts.children_in_branch,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.children_in_branch.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>ጠቅላላ የአጥቢያይቱ ሥርጭት ጣቢያዎች የሚገኙ ምእመናን ብዛት</Typography>
              </TableCell>
              <TableCell colSpan={3}>
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.total_in_branches}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>በአጥቢያይቱ የሚገኙ ጠቅላላ የምዕመናን ብዛት ድምር</Typography>
              </TableCell>
              <TableCell colSpan={3}>
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.memberCounts.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {!reportId && (
        <Stack alignItems="flex-start">
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSectionComplete(0)}
          >
            ቀጥል
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
